// src/utils/fileOperations.js
import { systemAPI } from '../services/api';

export const openFolder = async (folderPath) => {
  if (!folderPath) return false;

  const isMac = window.navigator.platform.includes('Mac') || window.navigator.platform === 'Darwin';
  const isWindows = window.navigator.platform === 'Win32';

  // 0. Path Normalization
  // Convert backslashes and handle Windows drive letters for non-Windows clients
  let normalizedPath = folderPath;
  if (!isWindows) {
    normalizedPath = folderPath.replace(/\\/g, '/');
    if (normalizedPath.match(/^[a-zA-Z]:/) && !normalizedPath.startsWith('/')) {
      normalizedPath = '/' + normalizedPath;
    }
  }

  const protocolUrl = `myemr://open?path=${encodeURIComponent(normalizedPath)}`;

  // Web environment logic
  if (!window.__TAURI__ && !window.electron) {
    // 1. Primary attempt: Custom Protocol Handler via Persistent Iframe
    // This solves the issue where the browser prompt causes the request to be cancelled
    // if the iframe is removed too quickly.
    try {
      console.log(`[Client] Attempting custom protocol: "${protocolUrl}"`);
      
      let iframe = document.getElementById('myemr-protocol-handler-iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'myemr-protocol-handler-iframe';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
      }
      
      iframe.src = protocolUrl;
      
      // On macOS, we prioritize the protocol handler and return early to avoid redundant UI.
      // Since it's a persistent iframe, it remains in the DOM to handle the OS response.
      if (isMac) {
        return true;
      }
    } catch (e) {
      console.warn('[Client] Custom protocol attempt failed:', e);
    }

    // 2. Server bridge (Fallback for Mac, Primary/Fallback for others)
    try {
      console.log(`[Client] Attempting server-side folder bridge for: "${normalizedPath}"`);
      const response = await systemAPI.openFolder(normalizedPath);
      if (response.data && response.data.success) {
        console.log('[Client] Server-side folder bridge succeeded.');
        return true;
      }
    } catch (bridgeError) {
      const errorDetail = bridgeError.response?.data?.error || bridgeError.message;
      console.warn(`[Client] Server-side folder bridge failed: ${errorDetail}`);
      window._lastFolderError = errorDetail; // Store for fallback window
    }

    // 3. Fallback to manual instructions window (only if everything else fails)
    try {
      // Re-create the fileUrl for the fallback UI
      let formattedPath = folderPath.replace(/\\/g, '/');
      if (formattedPath.match(/^[a-zA-Z]:/)) {
        formattedPath = `/${formattedPath}`;
      }
      const safeFileUrl = `file://${formattedPath}`;
      const escapedFileUrl = safeFileUrl.replace(/'/g, "\\'");
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Patient Folder</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              max-width: 800px; 
              margin: 0 auto; 
              padding: 40px 20px;
              text-align: center;
              background-color: #0f111a;
              color: #fff;
            }
            .container {
              background: #1a1d26;
              padding: 30px;
              border-radius: 12px;
              box-shadow: 0 10px 30px rgba(0,0,0,0.3);
              border: 1px solid #2c313d;
            }
            .path {
              background: #0f111a; 
              padding: 15px; 
              border-radius: 6px; 
              margin: 20px 0;
              word-break: break-all;
              font-family: 'Consolas', 'Monaco', monospace;
              color: #2c7be5;
              border: 1px solid #232833;
            }
            button {
              padding: 12px 24px;
              background: #2c7be5;
              color: white;
              border: none;
              border-radius: 6px;
              cursor: pointer;
              font-size: 16px;
              font-weight: 600;
              transition: all 0.2s;
            }
            button:hover {
              background: #1a68d1;
              transform: translateY(-1px);
            }
            button:active {
              transform: translateY(0);
            }
            #copyStatus {
              min-height: 24px;
              margin-top: 15px;
              font-size: 14px;
              color: #00d97e;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h2 style="margin-top: 0;">Patient Folder Location</h2>
            <p style="color: #8892b0;">The browser cannot open local folders directly for security reasons. Please use the link below:</p>
            <div class="path" id="folderPath">
              <a href="${safeFileUrl}" target="_blank" style="color: #2c7be5; text-decoration: underline; word-break: break-all;">
                ${safeFileUrl}
              </a>
            </div>
            <button onclick="copyToClipboard()" style="margin-top: 15px;">Copy Link to Clipboard</button>
            <p id="copyStatus"></p>
            ${window._lastFolderError ? `<p style="color: #667; font-size: 12px; margin-top: 20px;">Technical note: ${window._lastFolderError}</p>` : ''}
          </div>
          
          <script>
            function copyToClipboard() {
              const text = '${escapedFileUrl}';
              try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(text).then(() => {
                    showSuccess();
                  }).catch(() => {
                    fallbackCopy(text);
                  });
                } else {
                  fallbackCopy(text);
                }
              } catch (err) {
                fallbackCopy(text);
              }
            }
            
            function showSuccess() {
              const status = document.getElementById('copyStatus');
              status.textContent = '✓ Path copied to clipboard!';
              status.style.color = '#00d97e';
              setTimeout(() => { status.textContent = ''; }, 3000);
            }
            
            function fallbackCopy(text) {
              try {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                textArea.style.top = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (successful) {
                  showSuccess();
                } else {
                  showError();
                }
              } catch (err) {
                showError();
              }
            }
            
            function showError() {
              const status = document.getElementById('copyStatus');
              status.textContent = 'Failed to copy. Please select the text and copy manually.';
              status.style.color = '#e63757';
            }
          </script>
        </body>
        </html>
      `;
      
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      } else {
        const userConfirmed = window.confirm(
          `Could not open folder.\n\n` +
          `Please use this path in your file explorer:\n${folderPath}\n\n` +
          `Click OK to copy this path to your clipboard.`
        );
        if (userConfirmed) {
          await navigator.clipboard.writeText(folderPath);
          alert('Folder path copied to clipboard!');
        }
      }
      return true;
    } catch (error) {
      console.error('Error handling folder path in web:', error);
      return false;
    }
  }
  
  // Tauri specific code
  if (window.__TAURI__) {
    try {
      const { invoke } = window.__TAURI__.tauri;
      
      // Use the Tauri shell plugin to open the folder
      if (window.navigator.platform.includes('Mac') || window.navigator.platform === 'Darwin') {
        // On macOS, use the custom protocol handler
        await invoke('tauri', {
          __tauriModule: 'Shell',
          message: {
            cmd: 'execute',
            program: 'open',
            args: [protocolUrl],
            onEvent: () => {}
          }
        });
      } else if (window.navigator.platform === 'Win32') {
        // On Windows, use 'explorer /select,'
        await invoke('tauri', {
          __tauriModule: 'Shell',
          message: {
            cmd: 'execute',
            program: 'explorer',
            args: ['/select,', folderPath],
            onEvent: () => {}
          }
        });
      } else {
        // On Linux, use 'xdg-open'
        await invoke('tauri', {
          __tauriModule: 'Shell',
          message: {
            cmd: 'execute',
            program: 'xdg-open',
            args: [folderPath],
            onEvent: () => {}
          }
        });
      }
      
      return true;
    } catch (error) {
      console.error('Error opening folder in Tauri:', error);
      return false;
    }
  } else if (window.electron) {
    // Existing Electron fallback
    try {
      if (window.navigator.platform.includes('Mac') || window.navigator.platform === 'Darwin') {
        await window.electron.shell.openExternal(protocolUrl);
      } else {
        await window.electron.shell.showItemInFolder(folderPath);
      }
      return true;
    } catch (error) {
      console.error('Error opening folder in Electron:', error);
      return false;
    }
  } else {
    // Web fallback
    try {
      window.open(`file://${folderPath.startsWith('/') ? '' : '/'}${folderPath}`, '_blank');
      return true;
    } catch (error) {
      console.error('Error opening folder in browser:', error);
      return false;
    }
  }
};

/**
 * Copy text to clipboard using the Web API
 * @param {string} text - The text to copy to the clipboard
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const copyToClipboard = async (text) => {
  try {
    // Try the modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';  // Avoid scrolling to bottom
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (!successful) {
        throw new Error('Failed to copy using execCommand');
      }
      return true;
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};